import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Switch from "antd/es/switch";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { defineMessages, useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import OptionValue from "@mapmycustomers/shared/types/customField/OptionValue";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import PinLegend, {
  OptionHistogramData,
  SimpleClassificationColorItem,
} from "@mapmycustomers/shared/types/map/PinLegend";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import { DEFAULT_NO_VALUE_COLOR } from "@app/scene/map/components/pinLegends/utils/consts";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import { isDefaultColorOption } from "@app/util/pinLegend";

import ColorBadge from "../../ColorBadge/ColorBadge";

import ColorKey from "./ColorKey";
import styles from "./CustomOptionColorKey.module.scss";
import useOptionHistogramChartConfig from "./useOptionHistogramChartConfig";

const messages = defineMessages({
  count: {
    id: "map.layer.colorKey.legend.customOption.optionList.item",
    defaultMessage: "{label} <span>({count})</span>",
    description: "An option to show in a list of options for custom color key",
  },
  displayChart: {
    id: "map.layer.colorKey.legend.customOption.displayChartToggle",
    defaultMessage: "Display graph",
    description: "Label of the graph toggle switch in Custom Option Color legend",
  },
  unknown: {
    id: "map.layer.colorKey.legend.customOption.optionList.default",
    defaultMessage: "No value",
    description: "A default option name in a list of options for custom color key",
  },
});

interface Props {
  entityType: EntityTypesSupportedByMapsPage;
  field: IField;
  funnels: Funnel[];
  funnelStages: Record<Funnel["id"], Stage[]>;
  pinLegend: PinLegend;
}

const CustomOptionColorKey: React.FC<Props> = ({ field, funnels, funnelStages, pinLegend }) => {
  const intl = useIntl();

  const [chartVisible, setChartVisibility] = useState(false);
  const toggleChartVisibility = useCallback(() => setChartVisibility((visible) => !visible), []);
  const handleSwitchChange = useCallback(
    (checked: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
      stopEvents(event);
      setChartVisibility(checked);
    },
    []
  );

  const stageById: Map<Stage["id"], Stage> = useMemo(
    () =>
      new Map(
        Object.values(funnelStages).flatMap((stages) => stages.map((stage) => [stage.id, stage]))
      ),
    [funnelStages]
  );

  const getLabel = useCallback(
    (colorItem: SimpleClassificationColorItem): string => {
      if (isCustomField(field) && field.isOptionField) {
        const option = field.customFieldData.options.find(
          (option) => option.value == colorItem.fieldValue
        );
        if (option) {
          return option.displayName;
        }
      } else if (field.hasFeature(FieldFeature.FUNNEL_FIELD)) {
        const funnel = funnels.find(({ id }) => colorItem.fieldValue == id);
        if (funnel) {
          return funnel.name;
        }
      } else if (field.hasFeature(FieldFeature.STAGE_FIELD)) {
        const stage = stageById.get(parseInt(colorItem.fieldValue as string, 10));
        if (stage) {
          return `${stage.funnel.name} / ${stage.name}`;
        }
      }
      // otherwise just use fieldValue as label
      return colorItem.fieldValue as string;
    },
    [field, funnels, stageById]
  );

  const sortedColors = useMemo(() => {
    const colors = ((pinLegend.thematic?.colors ?? []) as SimpleClassificationColorItem[]).filter(
      (colorItem) => !isDefaultColorOption(colorItem)
    );
    if (!isCustomField(field)) {
      return colors;
    }

    const optionsOrderMap = field.customFieldData.options.reduce(
      (result, { displayOrder, value }) => {
        result[value] = displayOrder;
        return result;
      },
      {} as Record<OptionValue["value"], OptionValue["displayOrder"]>
    );
    return colors.sort(
      (a, b) =>
        optionsOrderMap[(a as SimpleClassificationColorItem).fieldValue as OptionValue["value"]] -
        optionsOrderMap[(b as SimpleClassificationColorItem).fieldValue as OptionValue["value"]]
    );
  }, [pinLegend.thematic?.colors, field]);

  const colorItemByLabel = useMemo(() => {
    return new Map(sortedColors.map((item) => [getLabel(item), item]));
  }, [getLabel, sortedColors]);

  const getColor = useCallback(
    (key: number | string) => {
      const colorItem = colorItemByLabel.get(String(key));
      return colorItem?.color ?? DEFAULT_NO_VALUE_COLOR;
    },
    [colorItemByLabel]
  );

  const data = useMemo(() => {
    const fieldValueToName = new Map(
      sortedColors.map((color) => [String(color.fieldValue), getLabel(color)])
    );
    // change key to a label
    return ((pinLegend.stats?.data?.histogram?.histogram as OptionHistogramData) ?? []).map(
      (item) => ({
        ...item,
        key: fieldValueToName.get(String(item.key)) ?? item.key,
      })
    );
  }, [getLabel, pinLegend.stats?.data?.histogram?.histogram, sortedColors]);

  const chartConfig = useOptionHistogramChartConfig({
    data,
    getColor,
    visible: chartVisible,
  });

  const countByFieldValue = (
    (pinLegend.stats?.data?.histogram?.histogram ?? []) as OptionHistogramData
  )?.reduce((result, item) => {
    result[item.key] = item.count;
    const colorItem = colorItemByLabel.get(String(item.key));
    if (colorItem) {
      result[colorItem.fieldValue as string] = item.count;
    }
    return result;
  }, {} as Record<string, number>);

  return (
    <ColorKey contentClassName={styles.content}>
      {chartVisible && (
        <HighchartsReact
          containerProps={{ className: styles.chart }}
          highcharts={Highcharts}
          isPureConfig
          options={chartConfig}
        />
      )}
      <div className={styles.optionList}>
        {(pinLegend.thematic?.colors ?? [])
          .filter((colorItem) => isDefaultColorOption(colorItem))
          .map((colorItem) => (
            <div key="default">
              <ColorBadge
                color={colorItem.color}
                label={intl.formatMessage(messages.count, {
                  count: pinLegend.stats?.data?.histogram?.missing ?? 0,
                  label: intl.formatMessage(messages.unknown),
                  span: (text: ReactNode[]) => <span className={styles.count}>{text}</span>,
                })}
              />
            </div>
          ))}
        {sortedColors.map((colorItem, index) => (
          <div key={index}>
            <ColorBadge
              color={colorItem.color}
              label={intl.formatMessage(messages.count, {
                count: countByFieldValue[colorItem.fieldValue as string] ?? 0,
                label: getLabel(colorItem),
                span: (text: ReactNode[]) => <span className={styles.count}>{text}</span>,
              })}
            />
          </div>
        ))}
      </div>
      {pinLegend.stats?.data?.histogram && (
        <div className={styles.chartToggle} onClick={toggleChartVisibility}>
          <span>{intl.formatMessage(messages.displayChart)}</span>
          <Switch checked={chartVisible} onChange={handleSwitchChange} />
        </div>
      )}
    </ColorKey>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
});

export default connect(mapStateToProps)(CustomOptionColorKey);
